import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { usePipelineContext } from '@/context/pipelineContext';
import { useAdminModeAccess } from '@/hooks/useAdminModeAccess';

/**
 * Resolves owner and client-owner Typesense filters from instant search state.
 */
export const useInstantSearchOwnerFilters = (slugOverride) => {
	const { pathname } = useLocation();
	const slug = slugOverride ?? pathname;
	const { isAdminMode } = useAdminModeAccess();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { filters: instantSearchFilters, filterSchema: instantSearchFilterSchema } = useSelector(
		(state) => state.instantSearch,
	);

	const filterSchema = useMemo(
		() => instantSearchFilterSchema[slug] ?? {},
		[instantSearchFilterSchema, slug],
	);
	const filters = useMemo(() => instantSearchFilters[slug] ?? {}, [instantSearchFilters, slug]);

	// Placeholder that can never match a real Firestore doc id, so the filter
	// resolves to zero results (rather than erroring on an empty filter value)
	// while userDetails is still loading.
	const userId = userDetails?.userId ?? '__none__';
	const canViewGroup = userDetails?.canViewGroup;

	const owners = useMemo(() => {
		let data = (!canViewGroup || !filters.owners?.length ? [userId] : [filters.owners])
			.flat()
			.filter((id) => id != null && id !== '');
		if (data.length === 0) {
			data = [userId];
		}
		return data;
	}, [canViewGroup, filters.owners, userId]);

	const clientOwners = useMemo(
		() => [filters?.clientOwners ?? []].flat().filter(Boolean),
		[filters?.clientOwners],
	);

	const ownerFilter = useMemo(
		() => (isAdminMode ? '' : `ownerDocId: [${owners.join(',')}]`),
		[isAdminMode, owners],
	);

	const clientOwnerFilter = useMemo(
		() => (clientOwners.length > 0 ? `clientOwnerDocId: [${clientOwners.join(',')}]` : ''),
		[clientOwners],
	);

	const baseFilters = useMemo(
		() => [ownerFilter, clientOwnerFilter].filter(Boolean),
		[ownerFilter, clientOwnerFilter],
	);

	return {
		slug,
		filters,
		filterSchema,
		owners,
		clientOwners,
		baseFilters,
		ownerFilter,
		clientOwnerFilter,
	};
};

export const mapStageIds = (stageData = []) =>
	stageData.map((s) => (typeof s === 'string' || typeof s === 'number' ? s : s?.id));

/**
 * Resolves deal table Typesense filters from instant search state.
 */
export const useInstantSearchDealFilters = (pipeline, tab) => {
	const { filters } = useInstantSearchOwnerFilters();

	const isEndorsement = pipeline == process.env.REACT_APP_PIPELINE_ENDORSEMENTS;

	const renewalsPipeline = process.env.REACT_APP_PIPELINE_RENEWALS;

	const {
		stages,
		active: activeStageData,
		inactive: inactiveStageData,
		closed: closedStageData,
		completed: completedStageData,
	} = usePipelineContext(isEndorsement ? null : pipeline);

	const active = useMemo(() => mapStageIds(activeStageData), [activeStageData]);
	const inactive = useMemo(() => mapStageIds(inactiveStageData), [inactiveStageData]);
	const closed = useMemo(() => mapStageIds(closedStageData), [closedStageData]);
	const completed = useMemo(() => mapStageIds(completedStageData), [completedStageData]);

	const isInvoicedTab = tab === 'invoiced';

	const pipelineStrings = useMemo(
		() => [process.env.REACT_APP_PIPELINE_NEW_BUSINESS, renewalsPipeline].join(','),
		[renewalsPipeline],
	);

	const endorsementStages = useMemo(
		() =>
			stages
				?.filter(
					(s) =>
						[process.env.REACT_APP_PIPELINE_NEW_BUSINESS, renewalsPipeline].includes(
							s.pipelineId,
						) && s.label?.toLowerCase()?.includes('endorsement'),
				)
				?.map((s) => s.id) ?? [],
		[stages, pipelineStrings, renewalsPipeline],
	);

	const getStageIdsForTab = useMemo(() => {
		// Active (not closed) stages include the invoiced stage, which belongs on the invoiced tab
		const stageIds = isInvoicedTab
			? completed
			: active.filter((stageId) => !inactive.includes(stageId));
		return {
			all: stageIds,
			excludingEndorsements: stageIds.filter(
				(stageId) => !endorsementStages.includes(stageId),
			),
		};
	}, [isInvoicedTab, completed, active, inactive, endorsementStages]);

	const dateRangeFilter = useMemo(() => {
		const renewalDateFilters = filters?.renewalDate || [];
		return renewalDateFilters.length > 0 ? renewalDateFilters[0] : '';
	}, [filters?.renewalDate]);

	const prospectFilter = filters?.isProspect?.length > 0 ? filters.isProspect.join(' && ') : '';

	const claimsFilters = useMemo(() => {
		const stageIds = (tab == 'active' ? active : closed).join(',');
		const stageFilter = `dealStage: [${stageIds}]`;

		return [`pipeline:=${process.env.REACT_APP_PIPELINE_CLAIMS}`, stageFilter];
	}, [tab, active, closed]);

	const endorsementsFilters = useMemo(() => {
		const tabStageIds = getStageIdsForTab.all;
		const tabStages = `dealStage: [${tabStageIds.join(',')}]`;

		const isEndorsementStage =
			endorsementStages.length > 0
				? `(pipeline: [${pipelineStrings}] && dealStage: [${endorsementStages.join(',')}])`
				: '';

		const isEndorsementPipeline = `(pipeline:=${process.env.REACT_APP_PIPELINE_ENDORSEMENTS} && ${tabStages})`;

		const endorsementsFilter =
			isInvoicedTab || endorsementStages.length === 0
				? isEndorsementPipeline
				: `(${isEndorsementPipeline} || ${isEndorsementStage})`;

		return [endorsementsFilter, ...(prospectFilter ? [prospectFilter] : [])];
	}, [
		getStageIdsForTab,
		isInvoicedTab,
		prospectFilter,
		endorsementStages,
		pipelineStrings,
	]);

	const newBusinessFilters = useMemo(() => {
		const stageIds = getStageIdsForTab.excludingEndorsements.join(',');
		const stageFilter = `dealStage: [${stageIds}]`;
		const nbPipeline = `pipeline:=${process.env.REACT_APP_PIPELINE_NEW_BUSINESS}`;

		return [stageFilter, nbPipeline, ...(prospectFilter ? [prospectFilter] : [])];
	}, [getStageIdsForTab, endorsementStages, prospectFilter]);

	const renewalsFilters = useMemo(() => {
		const stageIds = getStageIdsForTab.excludingEndorsements.join(',');
		const stageFilter = `dealStage: [${stageIds}]`;
		const renewalPipeline = `pipeline:=${process.env.REACT_APP_PIPELINE_RENEWALS}`;

		return [
			renewalPipeline,
			stageFilter,
			...(dateRangeFilter ? [dateRangeFilter] : []),
		];
	}, [getStageIdsForTab, pipeline, dateRangeFilter]);

	return {
		renewalsFilters,
		claimsFilters,
		endorsementsFilters,
		newBusinessFilters,
	};
};
